/**
 * 数据同步服务
 * 管理本地存储与自托管云端存储的双向同步
 * 支持自动同步和手动同步
 */

import { StorageKeys } from '../utils/storageAdapter';
import { LocalStorageAdapter } from '../utils/storageAdapter';
import { HttpStorageAdapter } from '../utils/httpStorageAdapter';
import { isApiConfigured } from '../utils/apiClient';
import { getCurrentUser } from './authService';

// 同步配置类型
export interface SyncSettings {
    /** 是否启用自动同步 */
    autoSync: boolean;
    /** 自动同步频次（分钟） */
    syncInterval: number;
    /** 仅在WiFi下同步 */
    syncOnWifiOnly: boolean;
    /** 同步时包含的数据类型 */
    syncDataTypes: SyncDataType[];
    /** 上次同步时间 */
    lastSyncTime: string | null;
    /** 同步冲突解决策略 */
    conflictResolution: 'local' | 'cloud' | 'timestamp';
    /** 是否启用离线模式 */
    offlineMode: boolean;
}

// 可同步的数据类型
export type SyncDataType = 
    | 'profile'      // 用户配置
    | 'schedule'     // 用餐计划
    | 'shopping'     // 购物清单
    | 'inventory'    // 食材库存
    | 'recipes'      // 自定义食谱
    | 'favorites'    // 收藏
    | 'ingredients'; // 自定义食材

// 同步状态
export type SyncStatus = 
    | 'idle'         // 空闲
    | 'syncing'      // 同步中
    | 'success'      // 同步成功
    | 'error'        // 同步失败
    | 'offline';     // 离线状态

// 同步结果
export interface SyncResult {
    status: SyncStatus;
    timestamp: string;
    details?: {
        uploaded: number;
        downloaded: number;
        conflicts: number;
        errors: string[];
    };
    error?: string;
}

// 默认同步配置
export const DEFAULT_SYNC_SETTINGS: SyncSettings = {
    autoSync: true,
    syncInterval: 15, // 15分钟
    syncOnWifiOnly: false,
    syncDataTypes: ['profile', 'schedule', 'shopping', 'inventory', 'recipes', 'favorites'],
    lastSyncTime: null,
    conflictResolution: 'timestamp',
    offlineMode: false,
};

// 同步选项
export interface SyncOptions {
    forceFullSync?: boolean;
    dataTypes?: SyncDataType[];
    direction?: 'push' | 'pull' | 'both';
}

/**
 * 同步管理器类
 * 负责本地数据持久化和云端同步的协调
 */
export class SyncManager {
    private localAdapter: LocalStorageAdapter;
    private cloudAdapter: HttpStorageAdapter | null = null;
    private syncSettings: SyncSettings = { ...DEFAULT_SYNC_SETTINGS };
    private syncIntervalId: number | null = null;
    private syncStatus: SyncStatus = 'idle';
    private syncListeners: Set<(status: SyncStatus, result?: SyncResult) => void> = new Set();
    private isInitialized = false;

    constructor() {
        this.localAdapter = new LocalStorageAdapter('');
        this.generateDeviceId();
    }

    /**
     * 生成设备唯一ID
     */
    private generateDeviceId(): string {
        const stored = localStorage.getItem('foodbook:deviceId');
        if (stored) return stored;
        
        const newId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('foodbook:deviceId', newId);
        return newId;
    }

    /**
     * 初始化同步管理器
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) return;

        console.log('[SyncManager] 初始化同步管理器...');

        // 加载同步配置
        await this.loadSyncSettings();

        // 初始化云端适配器
        try {
            if (isApiConfigured()) {
                this.cloudAdapter = new HttpStorageAdapter();
            } else {
                console.warn('[SyncManager] 未配置自托管 API，同步功能将保持本地模式');
            }
        } catch (error) {
            console.warn('[SyncManager] 云端适配器初始化失败:', error);
        }

        // 如果启用自动同步，启动定时器
        if (this.syncSettings.autoSync && !this.syncSettings.offlineMode) {
            this.startAutoSync();
        }

        // 监听网络状态变化
        this.setupNetworkListeners();

        this.isInitialized = true;
        console.log('[SyncManager] 初始化完成');
    }

    /**
     * 设置网络状态监听
     */
    private setupNetworkListeners(): void {
        if (typeof window === 'undefined') return;

        window.addEventListener('online', () => {
            console.log('[SyncManager] 网络已连接');
            if (this.syncSettings.autoSync) {
                this.syncToCloud();
            }
        });

        window.addEventListener('offline', () => {
            console.log('[SyncManager] 网络已断开');
            this.syncStatus = 'offline';
            this.notifyListeners();
        });
    }

    /**
     * 加载同步配置
     */
    private async loadSyncSettings(): Promise<void> {
        try {
            const saved = await this.localAdapter.get<SyncSettings>('foodbook:sync:settings');
            if (saved) {
                this.syncSettings = { ...DEFAULT_SYNC_SETTINGS, ...saved };
            }
        } catch (error) {
            console.warn('[SyncManager] 加载同步配置失败:', error);
        }
    }

    /**
     * 保存同步配置
     */
    async saveSyncSettings(settings: Partial<SyncSettings>): Promise<void> {
        this.syncSettings = { ...this.syncSettings, ...settings };
        await this.localAdapter.set('foodbook:sync:settings', this.syncSettings);

        // 如果修改了自动同步设置，重启定时器
        if (settings.autoSync !== undefined || settings.syncInterval !== undefined) {
            this.stopAutoSync();
            if (this.syncSettings.autoSync && !this.syncSettings.offlineMode) {
                this.startAutoSync();
            }
        }
    }

    /**
     * 获取当前同步配置
     */
    getSyncSettings(): SyncSettings {
        return { ...this.syncSettings };
    }

    /**
     * 获取当前同步状态
     */
    getSyncStatus(): SyncStatus {
        return this.syncStatus;
    }

    /**
     * 启动自动同步
     */
    startAutoSync(): void {
        if (this.syncIntervalId !== null) return;
        if (typeof window === 'undefined') return;

        const intervalMs = this.syncSettings.syncInterval * 60 * 1000;
        
        console.log(`[SyncManager] 启动自动同步，间隔: ${this.syncSettings.syncInterval}分钟`);
        
        this.syncIntervalId = window.setInterval(() => {
            this.performAutoSync();
        }, Math.max(intervalMs, 60000)); // 最少1分钟
    }

    /**
     * 停止自动同步
     */
    stopAutoSync(): void {
        if (this.syncIntervalId !== null) {
            clearInterval(this.syncIntervalId);
            this.syncIntervalId = null;
            console.log('[SyncManager] 自动同步已停止');
        }
    }

    /**
     * 执行自动同步
     */
    private async performAutoSync(): Promise<void> {
        // 检查网络状态
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            console.log('[SyncManager] 离线状态，跳过自动同步');
            return;
        }

        // 检查是否仅在WiFi下同步
        if (this.syncSettings.syncOnWifiOnly) {
            const connection = (navigator as any).connection;
            if (connection && connection.effectiveType !== 'wifi') {
                console.log('[SyncManager] 非WiFi环境，跳过同步');
                return;
            }
        }

        console.log('[SyncManager] 执行自动同步...');
        await this.syncToCloud();
    }

    /**
     * 手动同步到云端（双向）
     */
    async syncToCloud(options: SyncOptions = {}): Promise<SyncResult> {
        if (this.syncStatus === 'syncing') {
            return { status: 'syncing', timestamp: new Date().toISOString() };
        }

        this.syncStatus = 'syncing';
        this.notifyListeners();

        const result: SyncResult = {
            status: 'success',
            timestamp: new Date().toISOString(),
            details: { uploaded: 0, downloaded: 0, conflicts: 0, errors: [] }
        };

        try {
            const user = await getCurrentUser();
            if (!user) {
                throw new Error('用户未登录');
            }

            const dataTypes = options.dataTypes || this.syncSettings.syncDataTypes;

            // 同步各项数据
            for (const dataType of dataTypes) {
                try {
                    const syncResult = await this.syncDataType(dataType, user.id, options);
                    if (result.details) {
                        result.details.uploaded += syncResult.uploaded;
                        result.details.downloaded += syncResult.downloaded;
                        result.details.conflicts += syncResult.conflicts;
                    }
                } catch (error) {
                    console.error(`[SyncManager] 同步 ${dataType} 失败:`, error);
                    if (result.details) {
                        result.details.errors.push(`${dataType}: ${error}`);
                    }
                }
            }

            // 更新上次同步时间
            this.syncSettings.lastSyncTime = new Date().toISOString();
            await this.localAdapter.set('foodbook:sync:settings', this.syncSettings);

            this.syncStatus = result.details?.errors.length ? 'error' : 'success';
            
        } catch (error) {
            console.error('[SyncManager] 同步失败:', error);
            this.syncStatus = 'error';
            result.status = 'error';
            result.error = error instanceof Error ? error.message : String(error);
        }

        this.notifyListeners(result);
        return result;
    }

    /**
     * 从云端同步到本地
     */
    async syncFromCloud(options: SyncOptions = {}): Promise<SyncResult> {
        if (this.syncStatus === 'syncing') {
            return { status: 'syncing', timestamp: new Date().toISOString() };
        }

        this.syncStatus = 'syncing';
        this.notifyListeners();

        const result: SyncResult = {
            status: 'success',
            timestamp: new Date().toISOString(),
            details: { uploaded: 0, downloaded: 0, conflicts: 0, errors: [] }
        };

        try {
            const user = await getCurrentUser();
            if (!user) {
                throw new Error('用户未登录');
            }

            const dataTypes = options.dataTypes || this.syncSettings.syncDataTypes;

            // 从云端拉取各项数据
            for (const dataType of dataTypes) {
                try {
                    const syncResult = await this.pullDataType(dataType, user.id, options.forceFullSync);
                    if (result.details) {
                        result.details.downloaded += syncResult.downloaded;
                        result.details.conflicts += syncResult.conflicts;
                    }
                } catch (error) {
                    console.error(`[SyncManager] 拉取 ${dataType} 失败:`, error);
                    if (result.details) {
                        result.details.errors.push(`${dataType}: ${error}`);
                    }
                }
            }

            this.syncSettings.lastSyncTime = new Date().toISOString();
            await this.localAdapter.set('foodbook:sync:settings', this.syncSettings);

            this.syncStatus = result.details?.errors.length ? 'error' : 'success';

        } catch (error) {
            console.error('[SyncManager] 从云端同步失败:', error);
            this.syncStatus = 'error';
            result.status = 'error';
            result.error = error instanceof Error ? error.message : String(error);
        }

        this.notifyListeners(result);
        return result;
    }

    /**
     * 同步特定数据类型（双向）
     */
    private async syncDataType(
        dataType: SyncDataType, 
        userId: string, 
        options: SyncOptions
    ): Promise<{ uploaded: number; downloaded: number; conflicts: number }> {
        const result = { uploaded: 0, downloaded: 0, conflicts: 0 };

        // 获取本地数据
        const localData = await this.getLocalData(dataType, userId);
        
        // 获取云端数据
        const cloudData = await this.getCloudData(dataType, userId);

        // 比较并合并数据
        const { merged, conflicts } = this.mergeData(localData, cloudData, dataType);

        // 保存合并后的数据
        if (conflicts.length > 0 || options.forceFullSync || !localData || !cloudData) {
            await this.saveLocalData(dataType, userId, merged);
            await this.saveCloudData(dataType, userId, merged);
            result.uploaded = 1;
            result.downloaded = 1;
            result.conflicts = conflicts.length;
        }

        return result;
    }

    /**
     * 从云端拉取特定数据类型
     */
    private async pullDataType(
        dataType: SyncDataType,
        userId: string,
        _forceFullSync?: boolean
    ): Promise<{ downloaded: number; conflicts: number }> {
        const result = { downloaded: 0, conflicts: 0 };

        const cloudData = await this.getCloudData(dataType, userId);
        if (cloudData) {
            const localData = await this.getLocalData(dataType, userId);
            const { merged, conflicts } = this.mergeData(localData, cloudData, dataType);
            
            await this.saveLocalData(dataType, userId, merged);
            result.downloaded = 1;
            result.conflicts = conflicts.length;
        }

        return result;
    }

    /**
     * 获取本地数据
     */
    private async getLocalData(dataType: SyncDataType, userId: string): Promise<any> {
        const key = this.getStorageKey(dataType, userId);
        return await this.localAdapter.get(key);
    }

    /**
     * 保存本地数据
     */
    private async saveLocalData(dataType: SyncDataType, userId: string, data: any): Promise<void> {
        const key = this.getStorageKey(dataType, userId);
        await this.localAdapter.set(key, data);
    }

    /**
     * 获取云端数据
     */
    private async getCloudData(dataType: SyncDataType, userId: string): Promise<any> {
        if (!this.cloudAdapter) return null;
        
        const key = this.getStorageKey(dataType, userId);
        try {
            return await this.cloudAdapter.get(key);
        } catch (error) {
            console.warn(`[SyncManager] 获取云端数据失败: ${dataType}`, error);
            return null;
        }
    }

    /**
     * 保存云端数据
     */
    private async saveCloudData(dataType: SyncDataType, userId: string, data: any): Promise<void> {
        if (!this.cloudAdapter) return;

        const key = this.getStorageKey(dataType, userId);
        try {
            await this.cloudAdapter.set(key, data);
        } catch (error) {
            console.warn(`[SyncManager] 保存云端数据失败: ${dataType}`, error);
            throw error;
        }
    }

    /**
     * 获取存储键
     */
    private getStorageKey(dataType: SyncDataType, userId: string): string {
        switch (dataType) {
            case 'profile': return StorageKeys.userProfile(userId);
            case 'schedule': return StorageKeys.userSchedule(userId);
            case 'shopping': return StorageKeys.userShopping(userId);
            case 'inventory': return StorageKeys.userInventory(userId);
            case 'recipes': return StorageKeys.userRecipes(userId);
            case 'favorites': return StorageKeys.userFavorites(userId);
            case 'ingredients': return StorageKeys.userIngredients(userId);
            default: throw new Error(`未知数据类型: ${dataType}`);
        }
    }

    /**
     * 合并本地和云端数据
     */
    private mergeData(local: any, cloud: any, dataType: SyncDataType): { merged: any; conflicts: string[] } {
        const conflicts: string[] = [];

        // 如果一方为空，直接返回另一方
        if (!local) return { merged: cloud, conflicts };
        if (!cloud) return { merged: local, conflicts };

        // 根据数据类型选择合并策略
        switch (dataType) {
            case 'profile':
                return this.mergeProfile(local, cloud, conflicts);
            case 'schedule':
                return this.mergeSchedule(local, cloud, conflicts);
            case 'shopping':
            case 'inventory':
            case 'favorites':
                return this.mergeArrayData(local, cloud, conflicts);
            default:
                // 默认使用时间戳策略
                return this.mergeByTimestamp(local, cloud, conflicts);
        }
    }

    /**
     * 合并用户配置
     */
    private mergeProfile(local: any, cloud: any, conflicts: string[]): { merged: any; conflicts: string[] } {
        const strategy = this.syncSettings.conflictResolution;
        
        if (strategy === 'local') {
            return { merged: { ...cloud, ...local, updatedAt: new Date().toISOString() }, conflicts };
        } else if (strategy === 'cloud') {
            return { merged: { ...local, ...cloud, updatedAt: new Date().toISOString() }, conflicts };
        } else {
            // 时间戳策略
            const localTime = new Date(local.updatedAt || 0).getTime();
            const cloudTime = new Date(cloud.updatedAt || 0).getTime();
            
            if (localTime > cloudTime) {
                return { merged: { ...cloud, ...local }, conflicts };
            } else {
                return { merged: { ...local, ...cloud }, conflicts };
            }
        }
    }

    /**
     * 合并用餐计划
     */
    private mergeSchedule(local: any, cloud: any, conflicts: string[]): { merged: any; conflicts: string[] } {
        const merged: any = {};
        const allDates = new Set([...Object.keys(local), ...Object.keys(cloud)]);

        for (const date of allDates) {
            const localMeals = local[date] || [];
            const cloudMeals = cloud[date] || [];

            // 根据时间戳合并每餐
            const mealMap = new Map();
            
            [...localMeals, ...cloudMeals].forEach((meal: any) => {
                const key = `${meal.time}_${meal.title}`;
                const existing = mealMap.get(key);
                
                if (!existing) {
                    mealMap.set(key, meal);
                } else {
                    const existingTime = new Date(existing.updatedAt || 0).getTime();
                    const newTime = new Date(meal.updatedAt || 0).getTime();
                    
                    if (newTime > existingTime) {
                        mealMap.set(key, meal);
                    }
                }
            });

            merged[date] = Array.from(mealMap.values());
        }

        return { merged, conflicts };
    }

    /**
     * 合并数组数据（购物清单、库存、收藏等）
     */
    private mergeArrayData(local: any[], cloud: any[], conflicts: string[]): { merged: any[]; conflicts: string[] } {
        if (!Array.isArray(local)) return { merged: cloud || [], conflicts };
        if (!Array.isArray(cloud)) return { merged: local || [], conflicts };

        const merged = new Map();

        [...local, ...cloud].forEach((item: any) => {
            const key = item.id || item._id;
            if (!key) return;

            const existing = merged.get(key);
            if (!existing) {
                merged.set(key, item);
            } else {
                const existingTime = new Date(existing.updatedAt || 0).getTime();
                const newTime = new Date(item.updatedAt || 0).getTime();
                
                if (newTime > existingTime) {
                    merged.set(key, item);
                } else if (newTime === existingTime && JSON.stringify(item) !== JSON.stringify(existing)) {
                    conflicts.push(key);
                }
            }
        });

        return { merged: Array.from(merged.values()), conflicts };
    }

    /**
     * 基于时间戳合并
     */
    private mergeByTimestamp(local: any, cloud: any, conflicts: string[]): { merged: any; conflicts: string[] } {
        const localTime = new Date(local.updatedAt || local.lastModified || 0).getTime();
        const cloudTime = new Date(cloud.updatedAt || cloud.lastModified || 0).getTime();

        if (localTime >= cloudTime) {
            return { merged: local, conflicts };
        } else {
            return { merged: cloud, conflicts };
        }
    }

    /**
     * 添加同步状态监听器
     */
    addSyncListener(callback: (status: SyncStatus, result?: SyncResult) => void): () => void {
        this.syncListeners.add(callback);
        return () => this.syncListeners.delete(callback);
    }

    /**
     * 通知所有监听器
     */
    private notifyListeners(result?: SyncResult): void {
        this.syncListeners.forEach(callback => {
            try {
                callback(this.syncStatus, result);
            } catch (error) {
                console.error('[SyncManager] 监听器执行失败:', error);
            }
        });
    }

    /**
     * 获取上次同步时间
     */
    getLastSyncTime(): string | null {
        return this.syncSettings.lastSyncTime;
    }

    /**
     * 检查是否需要同步
     */
    needsSync(): boolean {
        if (!this.syncSettings.lastSyncTime) return true;
        
        const lastSync = new Date(this.syncSettings.lastSyncTime).getTime();
        const intervalMs = this.syncSettings.syncInterval * 60 * 1000;
        
        return Date.now() - lastSync > intervalMs;
    }

    /**
     * 导出本地数据（用于备份）
     */
    async exportLocalData(): Promise<Record<string, any>> {
        const user = await getCurrentUser();
        const userId = user?.id || 'anonymous';
        
        const exportData: Record<string, any> = {};
        
        for (const dataType of this.syncSettings.syncDataTypes) {
            const data = await this.getLocalData(dataType, userId);
            if (data) {
                exportData[dataType] = data;
            }
        }
        
        return exportData;
    }

    /**
     * 导入本地数据（用于恢复）
     */
    async importLocalData(data: Record<string, any>): Promise<void> {
        const user = await getCurrentUser();
        const userId = user?.id || 'anonymous';
        
        for (const [dataType, value] of Object.entries(data)) {
            if (this.syncSettings.syncDataTypes.includes(dataType as SyncDataType)) {
                await this.saveLocalData(dataType as SyncDataType, userId, value);
            }
        }
    }

    /**
     * 清除所有本地数据
     */
    async clearLocalData(): Promise<void> {
        await this.localAdapter.clear();
    }

    /**
     * 获取本地存储适配器（用于直接访问）
     */
    getLocalAdapter(): LocalStorageAdapter {
        return this.localAdapter;
    }

    /**
     * 销毁同步管理器
     */
    destroy(): void {
        this.stopAutoSync();
        this.syncListeners.clear();
        this.isInitialized = false;
    }
}

// 导出单例实例
export const syncManager = new SyncManager();

// 便捷导出
export default syncManager;
